import "client/styles/globals.css";
import type { AppProps } from "next/app";
import { appWithTranslation } from "next-i18next";
import Head from "next/head";
import Script from "next/script";
import { useAds } from "client/hooks/utils/useAds";
import "cropperjs/dist/cropper.min.css";
import "components/modules/Editors/ImageResizer/Preview/Preview.css";
import { cdnUrl, url } from "client/config/url";
import { useRouter } from "next/router";
import translatedRoutesSlugs from "translatedRouteSlugs";
import { languageCodes } from "components/modules/Internationalization/Internationalization";
import { getUrlFromPathname } from "client/utils/getUrlFromPathname";
import {
    bottomModalPortalContainerId,
    modalAfterBottomModalContainerId,
    modalPortalContainerId,
} from "client/config/divIds";
import { ThemeProvider } from "client/contexts/ThemeProvider";
import { Toaster } from "components/shadcn/toaster";
import { useRouteProtection } from "@/components/modules/Auth/useRouteProtection";
import { QueryClientProvider } from "@/contexts/QueryClientProvider";
import { getQueryClient } from "@/lib/queryClient";
import { Component, ErrorInfo, ReactNode } from "react";
import { OutdatedVersionModal } from "@/components/common/OutdatedVersionModal/OutdatedVersionModal";
import { gtag } from "@/utils/adsense/gtag";
import { useZohoDesk } from "../client/hooks/utils/useZohoDesk";

const MyApp = ({ Component, pageProps }: AppProps) => {
    const { onScriptLoad } = useAds();
    const { pathname: _pathname, locale, asPath } = useRouter();
    const router = useRouter();
    const pathname =
        _pathname === "/[format-converter]"
            ? asPath
            : _pathname
                  .replace(new RegExp("\\[", "g"), ":")
                  .replace(new RegExp("\\]", "g"), "")
                  .split("/")
                  .map((chunk) => {
                      if (chunk[0] === ":") {
                          return chunk.replace(/([-_][a-z])/g, (group: string) =>
                              group.toUpperCase().replace("-", "").replace("_", ""),
                          );
                      }
                      return chunk;
                  })
                  .join("/")
                  .toLowerCase();
    const canonical = `${url}${locale === "en" ? "" : `/${locale}`}${
        pathname === "/"
            ? ""
            : translatedRoutesSlugs[pathname]
              ? "/" + getUrlFromPathname(translatedRoutesSlugs[pathname]?.[locale ?? "en"] ?? "", router)
              : (router.asPath ?? pathname)
    }`;
    const client = getQueryClient();
    useZohoDesk();

    return (
        <>
            <script
                dangerouslySetInnerHTML={{
                    __html: `
                        const token = localStorage.getItem("token");
                        document.body.setAttribute("data-is-logged-in", token ? "true": "false");
                        try {
                        if (localStorage.theme === 'dark' || ((!('theme' in localStorage) || localStorage.theme === 'system') && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
                            document.documentElement.classList.add('dark');
                            document.documentElement.style.colorScheme = "dark";
                            document.documentElement.style.backgroundColor = "#1c1e25";
                          } else {
                            document.documentElement.classList.remove('dark');
                          }
                        } catch (_) {}
                     `,
                }}
            />
            <Script type="text/javascript" async={true} src={`${cdnUrl}/prebid.js`} />
            <Script
                async={true}
                src="https://www.googletagmanager.com/gtag/js?id=UA-144096646-1"
                onLoad={onScriptLoad}
            />
            <Script
                dangerouslySetInnerHTML={{
                    __html: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){
                            dataLayer.push(arguments);
                        }
                        gtag('js', new Date());
                        gtag('config', 'UA-144096646-1');
                `,
                }}
            />
            <Script
                type="text/javascript"
                id="zohodeskasap"
                dangerouslySetInnerHTML={{
                    __html: `var d=document;s=d.createElement("script"),s.type="text/javascript",s.id="zohodeskasapscript",s.defer=!0,s.nonce="{place_your_nonce_value_here}",s.src="https://desk.zohocloud.ca/portal/api/web/asapApp/7942000000211129?orgId=110001563481",t=d.getElementsByTagName("script")[0],t.parentNode.insertBefore(s,t),window.ZohoDeskAsapReady=function(s){
                    var e=window.ZohoDeskAsap__asyncalls=window.ZohoDeskAsap__asyncalls||[];window.ZohoDeskAsapReadyStatus?(s&&e.push(s),e.forEach(s=>s&&s()),window.ZohoDeskAsap__asyncalls=null):s&&e.push(s)};`,
                }}
            />
            <Head>
                <title>Image Resizer</title>
                <meta name="viewport" content="initial-scale=1.0, width=device-width, height=device-height" />
                <link rel="canonical" href={canonical} />
                <meta property="twitter:image" content="/social.png" />
                {languageCodes.map((code) => {
                    const slug =
                        _pathname === "/[format-converter]"
                            ? asPath.split("/")[1]
                            : pathname === "/"
                              ? ""
                              : (getUrlFromPathname((translatedRoutesSlugs as any)[pathname]?.[code], router) ?? "");
                    const href = `${url}${code === "en" ? "" : `/${code}`}${slug ? "/" : ""}${slug}`;
                    return <link key={href} rel="alternate" hrefLang={code} href={href} />;
                })}
                <link
                    rel="alternate"
                    hrefLang="x-default"
                    href={`${url}${pathname === "/" ? "" : getUrlFromPathname(pathname, router)}`}
                />
                <meta
                    property="og:url"
                    content={`${url}${locale ? (locale === "en" ? "" : `/${locale}`) : ""}${
                        pathname === "/"
                            ? ""
                            : "/" +
                              getUrlFromPathname(
                                  (translatedRoutesSlugs as any)[pathname]?.[locale ?? "en"] ??
                                      pathname?.replaceAll("/", ""),
                                  router,
                              )
                    }`}
                />
            </Head>
            <ErrorBoundary>
                <div id={modalPortalContainerId} />
                <div id={bottomModalPortalContainerId} />
                <div id={modalAfterBottomModalContainerId} />
                <div id="toasts_portal_container" />
                <div className="overflow-hidden text-black bg-white dark:bg-darkSurface-200">
                    <QueryClientProvider client={client}>
                        <Providers>
                            <ThemeProvider>
                                <Component {...pageProps} />
                                <Toaster />
                                <OutdatedVersionModal />
                            </ThemeProvider>
                        </Providers>
                    </QueryClientProvider>
                </div>
                <div data-aaad="true" data-aa-adunit="/275516369/IR_1x1"></div>
            </ErrorBoundary>
        </>
    );
};

const Providers = ({ children }: { children: ReactNode }) => {
    useRouteProtection();
    return <ThemeProvider>{children}</ThemeProvider>;
};

export default appWithTranslation(MyApp);

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(_: Error): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Caught by ErrorBoundary:", error, errorInfo);

        if (typeof window !== "undefined") {
            gtag("event", "client_exception", {
                event_category: "Error",
                event_label: window.location.href,
            });
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-white dark:bg-darkSurface-100 text-gray-800 dark:text-gray-100 px-4">
                    <div className="max-w-md text-center space-y-4">
                        <h1 className="text-3xl font-bold">Something went wrong</h1>
                        <p className="text-base text-gray-600 dark:text-gray-400">
                            An unexpected error occurred. Please refresh the page or try again later.
                        </p>
                        <button
                            onClick={() => {
                                const url = new URL(window.location.href);
                                url.searchParams.set("_cb", Date.now().toString());
                                window.location.href = url.toString();
                            }}
                            className="mt-4 px-4 py-2 rounded-lg bg-irBlue-600 text-white hover:bg-irBlue-200 transition"
                        >
                            Refresh Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
